import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import DistanceUnit from "@mapmycustomers/shared/enum/DistanceUnit";
import { MapEntity } from "@mapmycustomers/shared/types/entity";
import ActivitySuggestion from "@mapmycustomers/shared/types/entity/ActivitySuggestion";
import GeoPoint from "@mapmycustomers/shared/types/shapes/GeoPoint";
import { Modal } from "@mapmycustomers/ui";

import DistanceSelector from "@app/component/DistanceSelector";
import localSettings from "@app/config/LocalSettings";
import { RootState } from "@app/store/rootReducer";
import { METERS_IN_MILE } from "@app/util/consts";

import { areNearbyEntitiesLoading, getNearbyEntities } from "../../store";
import { fetchNearbyEntities } from "../../store/actions";

import Map from "./Map";
import styles from "./NearbyModal.module.scss";
import Title from "./Title";

export const DEFAULT_DISTANCE = {
  km: 10,
  mi: 5,
};

interface Props {
  loading?: boolean;
  nearbyEntities: MapEntity[];
  onFetchNearbyEntities: (payload: { distance: number; geoPoint: GeoPoint }) => void;
  onHide: () => void;
  suggestion: ActivitySuggestion;
}

const NearbyModal: React.FC<Props> = ({
  loading,
  nearbyEntities,
  onFetchNearbyEntities,
  onHide,
  suggestion,
}) => {
  const siUnits = localSettings.doesUseSiUnits();
  const [distance, setDistance] = useState(
    DEFAULT_DISTANCE[siUnits ? DistanceUnit.KM : DistanceUnit.MILE]
  );
  const intl = useIntl();

  useEffect(() => {
    if (suggestion.geoPoint) {
      onFetchNearbyEntities({
        distance: distance * (siUnits ? 1000 : METERS_IN_MILE),
        geoPoint: suggestion.geoPoint,
      });
    }
  }, [suggestion.geoPoint, onFetchNearbyEntities, distance, siUnits]);

  return (
    <Modal
      footer={null}
      onCancel={onHide}
      title={<Title suggestion={suggestion} />}
      // transition name must be empty, otherwise map or map controls might be not displayed:
      // https://stackoverflow.com/a/64416167/5346779
      transitionName=""
      visible
      width="80vw"
    >
      <DistanceSelector
        className={styles.distanceSelector}
        distance={distance}
        label={intl.formatMessage({
          id: "planner.nearbyRecords.suggestion",
          defaultMessage: "Records within",
          description: "Prefix in planner view nearby suggestion records",
        })}
        onChange={setDistance}
      />
      <Map
        distance={distance}
        entities={nearbyEntities}
        loading={loading}
        suggestion={suggestion}
      />
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  loading: areNearbyEntitiesLoading(state),
  nearbyEntities: getNearbyEntities(state),
});

const mapDispatchToProps = {
  onFetchNearbyEntities: fetchNearbyEntities.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(NearbyModal);
